import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { joinWaitingRoom } from "../../functions/joinWaitingRoom";
import { getMyTeamStatus } from "../../functions/getMyTeamStatus";
import "./teamWaiting.css";

const POLL_INTERVAL_MS = 4000;

function readStoredUserData() {
  try {
    const raw =
      localStorage.getItem("userData") || sessionStorage.getItem("userData") || "{}";
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
}

export default function TeamWaiting() {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const userData = useMemo(() => readStoredUserData(), []);

  const userId = String(user?.userId ?? userData?.userId ?? "").trim();
  const email = String(user?.email ?? userData?.email ?? "").trim();
  const name = String(user?.name ?? userData?.name ?? "").trim();

  const [status, setStatus] = useState({
    loading: true,
    assigned: false,
    teamId: null,
    teamNumber: null,
    members: [],
  });
  const [error, setError] = useState("");
  const pollRef = useRef(null);

  useEffect(() => {
    if (!userId || !adminToken) return undefined;
    let cancelled = false;

    const applyResult = (data) => {
      if (cancelled) return;
      setStatus({
        loading: false,
        assigned: Boolean(data?.assigned),
        teamId: data?.teamId ?? null,
        teamNumber: data?.teamNumber ?? null,
        members: Array.isArray(data?.members) ? data.members : [],
      });
    };

    const poll = async () => {
      try {
        const data = await getMyTeamStatus({ userId, token: adminToken });
        applyResult(data);
      } catch (e) {
        if (!cancelled) console.error("Failed to poll team status:", e);
      }
    };

    (async () => {
      try {
        const data = await joinWaitingRoom({ userId, email, name, token: adminToken });
        applyResult(data);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to join waiting room:", e);
          setError(e.message || "Failed to join waiting room");
          setStatus((prev) => ({ ...prev, loading: false }));
        }
      }
      pollRef.current = window.setInterval(poll, POLL_INTERVAL_MS);
    })();

    return () => {
      cancelled = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [userId, email, name, adminToken]);

  const handleStart = () => {
    if (!status.teamId || !userId) return;
    const params = new URLSearchParams({
      teamId: status.teamId,
      userId,
      source: process.env.REACT_APP_GAME_ENVIROMENT || "local",
    });
    navigate(`/spacegame?${params.toString()}`);
  };

  return (
    <div className="team-waiting-page">
      <div className="team-waiting-shell">
        {error ? <p className="team-waiting-error">{error}</p> : null}

        {status.loading ? (
          <div className="team-waiting-loader">
            <div className="team-waiting-spinner" />
            <p>Joining waiting room...</p>
          </div>
        ) : !status.assigned ? (
          <div className="team-waiting-loader">
            <div className="team-waiting-spinner" />
            <p>Waiting to be added to a team...</p>
          </div>
        ) : (
          <>
            <h1 className="team-waiting-title">Team</h1>
            <ul className="team-waiting-roster">
              {status.members.map((m) => (
                <li key={m.userId} className="team-waiting-member">
                  <span className="team-waiting-member-name">{m.name || m.userId}</span>
                  <span
                    className={`team-waiting-role-badge ${
                      m.role === "captain" ? "is-captain" : "is-member"
                    }`}
                  >
                    {m.role === "captain" ? "Captain" : "Member"}
                  </span>
                </li>
              ))}
            </ul>
            <button type="button" className="team-waiting-start-btn" onClick={handleStart}>
              Next
            </button>
          </>
        )}
      </div>
    </div>
  );
}
